/* eslint-disable no-unused-vars */

import { Optional } from "sonobello.utilities.react.mui";

interface LocalStorageConfig {
  /** The location key of the data value within storage. */
  key: string;
  /** Flag indicating that this item should be skipped when programmatically clearing storage.
   * @remarks If unset, this is treated as `false`.
   */
  preventClear?: boolean;
}

interface LegacyLocalStorageConfigs {
  /** Legacy key for contactId. Previously stored under "leadId". */
  contactId: LocalStorageConfig;
  /** Legacy key for contactResult. Previously stored under "leadResult". */
  contactResult: LocalStorageConfig;
  /** Legacy key for isDeviceLogged. Previously stored under "deviceLogged". */
  isDeviceLogged: LocalStorageConfig;
  /** Legacy key for patient. Previously stored under "customer". */
  patient: LocalStorageConfig;
  /** Legacy key for patientNoteContent. Previously stored under "customerNoteContent". */
  patientNoteContent: LocalStorageConfig;
  /** Legacy key for patientNoteId. Previously stored under "customerNoteId". */
  patientNoteId: LocalStorageConfig;
  /** Legacy key for reportedPatientExperiments. Previously stored under "reportedCustomerExperiments". */
  reportedPatientExperiments: LocalStorageConfig;
  /** Legacy key for reservation. Previously stored under "opsReservation". */
  reservation: LocalStorageConfig;
}

interface LocalStorageConfigs {
  /** If the experiment identifier was anonymized then its value is stored here. */
  anonymousExperimentId: LocalStorageConfig;
  /** The confirmed appointment that the patient has booked. */
  appointment: LocalStorageConfig;
  /** A record indicating if the prerequisites to request a calendar have been met. */
  calendarPrerequisitesMet: LocalStorageConfig;
  /** Patient Note record for the current session. */
  patientNoteContent: LocalStorageConfig;
  /** Center record for the current session. */
  center: LocalStorageConfig;
  /** Centers record for the current session. */
  centers: LocalStorageConfig;
  /** Record for the disqualifying reasons available to the session. */
  disqualifyingReasons: LocalStorageConfig;
  /** The current flow for the session. */
  flow: LocalStorageConfig;
  /** Flow Id record for the current session. */
  flowId: LocalStorageConfig;
  /** Flag indicating if the device used for this local session has been recorded. */
  isDeviceLogged: LocalStorageConfig;
  /** Contact Id record for the current session. */
  contactId: LocalStorageConfig;
  /** Contact Result record for the current session. */
  contactResult: LocalStorageConfig;
  /** The medical conclusions for the user. */
  medicalConclusions: LocalStorageConfig;
  /** The medical form record for the session. */
  medicalForm: LocalStorageConfig;
  /** A calendar slot that has been reserved for booking confirmation in the OPS process. */
  reservation: LocalStorageConfig;
  /** Patient record for the current session. */
  patient: LocalStorageConfig;
  /** The id of the created patient note for the current session. */
  patientNoteId: LocalStorageConfig;
  /** The current primitive state for the qualification form. */
  qualifyForm: LocalStorageConfig;
  /** The patient experiments that have been successfully reported to the server. */
  reportedPatientExperiments: LocalStorageConfig;
  /** The current step of the session. */
  step: LocalStorageConfig;
  /** The session key from the obx url used to start the session. */
  sessionKey: LocalStorageConfig;
  /** The connection info used to establish a SignalR connection. */
  signalRConnectionInfo: LocalStorageConfig;
  /** The current service that will be booked for the session. */
  service: LocalStorageConfig;
  /** The current context for user telemetry for the session. */
  telemetryContext: LocalStorageConfig;
  /** The component properties needed to fully render the 'Thank You' view. */
  thankYouView: LocalStorageConfig;
  /** Token record for the current session. */
  token: LocalStorageConfig;
  /** Record for the application version compatible with the cache. */
  version: LocalStorageConfig;
  /** Legacy key configurations retained for reading data cached under old key names. */
  legacy: LegacyLocalStorageConfigs;
}

/** Configurations for static cached objects using static keys. */
export const LocalStorageConfigs: LocalStorageConfigs = {
  anonymousExperimentId: { key: "anonymousExperimentId" },
  appointment: { key: "appointment" },
  calendarPrerequisitesMet: { key: "calendarPrerequisitesMet" },
  center: { key: "center" },
  centers: { key: "centers" },
  patientNoteId: { key: "patientNoteId" },
  disqualifyingReasons: { key: "disqualifyingReasons" },
  flow: { key: "flow" },
  flowId: { key: "flowId" },
  isDeviceLogged: { key: "isDeviceLogged" },
  contactId: { key: "contactId", preventClear: true },
  contactResult: { key: "contactResult" },
  medicalConclusions: { key: "medicalConclusions" },
  medicalForm: { key: "medicalForm" },
  reservation: { key: "reservation" },
  patient: { key: "patient" },
  patientNoteContent: { key: "patientNoteContent" },
  qualifyForm: { key: "qualifyForm" },
  reportedPatientExperiments: { key: "reportedPatientExperiments" },
  sessionKey: { key: "sessionKey", preventClear: true },
  signalRConnectionInfo: { key: "signalRConnectionInfo" },
  service: { key: "service" },
  step: { key: "step" },
  telemetryContext: { key: "telemetryContext" },
  thankYouView: { key: "thankYouView", preventClear: true },
  token: { key: "token" },
  version: { key: "version" },
  legacy: {
    // Intentionally using "leadId" as the key to maintain compatibility with existing data.
    contactId: { key: "leadId", preventClear: true },
    // Intentionally using "leadResult" as the key to maintain compatibility with existing data.
    contactResult: { key: "leadResult" },
    // Intentionally using "deviceLogged" as the key to maintain compatibility with existing data.
    isDeviceLogged: { key: "deviceLogged" },
    // Intentionally using "customer" as the key to maintain compatibility with existing data.
    patient: { key: "customer" },
    // Intentionally using "customerNoteContent" as the key to maintain compatibility with existing data.
    patientNoteContent: { key: "customerNoteContent" },
    // Intentionally using "customerNoteId" as the key to maintain compatibility with existing data.
    patientNoteId: { key: "customerNoteId" },
    // Intentionally using "reportedCustomerExperiments" as the key to maintain compatibility with existing data.
    reportedPatientExperiments: { key: "reportedCustomerExperiments" },
    // Intentionally using "opsReservation" as the key to maintain compatibility with existing data.
    reservation: { key: "opsReservation" }
  }
};

/** Save the given JSON to local storage using the given key. */
export const saveToLocal = (obj: unknown, key: string): void => {
  if (obj === undefined) localStorage.removeItem(key);
  else localStorage.setItem(key, JSON.stringify(obj));
};

/** Attempt to load an object from the cache with the given key. */
export const loadFromLocal = <T>(key: string): T | undefined => {
  const store = localStorage.getItem(key);
  if (!store || store === "undefined") return undefined;
  const value = JSON.parse(store);
  return !value ? undefined : (value as T);
};

/** Attempt to load an object from the cache, falling back to a legacy key if the primary key has no value. */
export const loadFromLocalWithFallback = <T>(key: string, legacyKey: string): T | undefined =>
  loadFromLocal<T>(key) ?? loadFromLocal<T>(legacyKey);

/** Attempt to load a raw string value from the cache with the given key. */
export const loadFromLocalRaw = (key: string): Optional<string> => localStorage.getItem(key) || undefined;

/** Attempt to load a raw string value from the cache, falling back to a legacy key if the primary key has no value. */
export const loadFromLocalRawWithFallback = (key: string, legacyKey: string): Optional<string> =>
  loadFromLocalRaw(key) ?? loadFromLocalRaw(legacyKey);

/** Clear local storage respecting established configurations. */
export const clearLocalStorage = (): void => {
  const { legacy, ...primary } = LocalStorageConfigs;
  const configs = [...Object.values(primary), ...Object.values(legacy)];
  Object.keys(localStorage).forEach((key: string) => {
    if (key && !configs.find(c => c.key === key)?.preventClear) localStorage.removeItem(key);
  });
  sessionStorage.clear();
};
